/**
 * The two kinds of hotel MANUAL request — the same quote loop, two subjects:
 *
 *   modify  Modify → Manual request: new dates and/or a room upgrade; the
 *           admin's quote is the new price (6 fees − what was paid).
 *   cancel  Cancel → Manual request: cancel the selected stays; the admin's
 *           quote is a REFUND quote (booking price − 3 deductions).
 *
 * Everything else — rounds in one row, approve / quote / re-quote, accept /
 * reject (also by the admin on the partner's behalf), edit, the file — is
 * shared; the pieces read their kind's wording from here.
 */

export const REQUEST_KIND = { MODIFY: "modify", CANCEL: "cancel" };

export const KIND_META = {
  modify: {
    kind: "modify",
    typeLabel: "Hotel — manual request",
    category: "modification",
    partnerType: "modify",
    subject: "Hotel modification",
    quoteNoun: "quote",
    quoteTitle: "Price quote",
    createQuote: "Create quote",
    editQuote: "Edit quote",
    completeAction: "Approve modification",
    completeTitle: "Approve modification?",
    completeText: (label) =>
      `There is no price change: the booking is modified as requested and request ${label} is completed. No quote goes to the partner.`,
    completeNotice: (id) => `Modification approved. Request ${id} is complete.`,
    completedLabel: "Modified",
    flow: { quote: "Quote Ready", newQuote: "New Quote", done: "Confirmed" },
    reviewHint: "No price change → approve the modification. Price changed → send a quote to the partner.",
    partnerWaiting: "With Zenvoya for review — a quote appears here if the price changes.",
  },
  cancel: {
    kind: "cancel",
    typeLabel: "Hotel — manual cancellation",
    category: "cancellation",
    partnerType: "cancel",
    subject: "Hotel cancellation",
    quoteNoun: "refund quote",
    quoteTitle: "Refund quote",
    createQuote: "Create refund quote",
    editQuote: "Edit refund quote",
    completeAction: "Confirm cancellation",
    completeTitle: "Confirm cancellation?",
    completeText: (label) =>
      `The hotels are cancelled under the booking's cancellation policy and request ${label} is completed. No refund quote goes to the partner.`,
    completeNotice: (id) => `Cancellation confirmed. Request ${id} is complete.`,
    completedLabel: "Hotel cancelled",
    flow: { quote: "Refund Quote", newQuote: "New Refund Quote", done: "Cancelled" },
    reviewHint: "Cancel as per policy → confirm the cancellation. Otherwise → send a refund quote to the partner.",
    partnerWaiting: "With Zenvoya for review — a refund quote appears here.",
  },
};

// The FLIGHT flow's cancellation requests (components/flightServicing) —
// the cancel loop with flight wording. Stamped "flight_cancel" by
// Api/flightModifyRequestAPI; kindOf() reads it as "cancel".
const FLIGHT_CANCEL_META = {
  ...KIND_META.cancel,
  typeLabel: "Flight — manual cancellation",
  subject: "Flight cancellation",
  subjectLabel: "Flights to cancel",
  completeText: (label) =>
    `The flights are cancelled under the booking's cancellation policy and request ${label} is completed. No refund quote goes to the partner.`,
  completedLabel: "Flight cancelled",
};

// The ACTIVITY flow's requests (components/activityServicing) — the same
// loop with activity wording. Stamped "activity_modify" / "activity_cancel"
// by Api/activityModifyRequestAPI; kindOf() reads them as "modify" / "cancel".
// A modify runs on the manual-request API: after the partner accepts, the
// SAME request comes back to Zenvoya to complete (with payment) — no price
// re-check round.
const ACTIVITY_MODIFY_META = {
  ...KIND_META.modify,
  typeLabel: "Activity — manual request",
  subject: "Activity modification",
  completeAction: "Complete modification",
  completeTitle: "Complete modification?",
  completeText: (label) =>
    `Confirm the change has been made with the supplier. The booking is updated as agreed and request ${label} is completed.`,
  completeNotice: (id) => `Modification completed. Request ${id} is done and the booking is updated.`,
  reviewHint: "Price unchanged → complete the modification. Price changed → send a quote to the partner.",
  partnerWaiting: "With Zenvoya — they agree the change with the supplier, and send a quote here if the price changes.",
  acceptedReview: "The partner accepted the quote — complete the modification once the supplier has made the change",
  acceptedPartner: "You accepted the quote — Zenvoya is completing the change with the supplier.",
  acceptedAdmin: "This quote was accepted — complete the modification from the request.",
  acceptedCard: "Accepted — waiting for Zenvoya to complete",
  acceptedNotice: (id, admin) =>
    admin
      ? `Quote accepted on the partner's behalf. Request ${id} is back with you to complete.`
      : `Quote accepted. Zenvoya now completes request ${id} with the supplier.`,
  quoteDone: "This quote was accepted and the modification is complete.",
};
// A cancel runs on the same API: after the partner accepts the refund quote,
// the same request comes back to Zenvoya to confirm; the refund goes to the
// partner wallet. Confirming without a quote asks for the refund there.
const ACTIVITY_CANCEL_META = {
  ...KIND_META.cancel,
  typeLabel: "Activity — manual cancellation",
  subject: "Activity cancellation",
  subjectLabel: "Activities to cancel",
  completeText: (label) =>
    `Confirm the activities have been cancelled with the supplier. They are cancelled on the booking, the refund goes to the partner wallet, and request ${label} is completed.`,
  completeNotice: (id) => `Cancellation confirmed. Request ${id} is done; the refund goes to the partner wallet.`,
  completedLabel: "Activity cancelled",
  reviewHint: "Refund agreed as per policy → confirm the cancellation. Otherwise → send a refund quote to the partner.",
  partnerWaiting: "With Zenvoya — they cancel with the supplier and send a refund quote here.",
  acceptedReview: "The partner accepted the refund quote — confirm the cancellation once the supplier has cancelled",
  acceptedPartner: "You accepted the refund quote — Zenvoya is cancelling with the supplier.",
  acceptedAdmin: "This refund quote was accepted — confirm the cancellation from the request.",
  acceptedCard: "Accepted — waiting for Zenvoya to confirm the cancellation",
  acceptedNotice: (id, admin) =>
    admin
      ? `Refund quote accepted on the partner's behalf. Request ${id} is back with you to confirm.`
      : `Refund quote accepted. Zenvoya now cancels request ${id} with the supplier.`,
  quoteDone: "This refund quote was accepted and the cancellation is complete.",
};

const PRODUCT_META = {
  flight_cancel: FLIGHT_CANCEL_META,
  activity_modify: ACTIVITY_MODIFY_META,
  activity_cancel: ACTIVITY_CANCEL_META,
};
const CANCEL_KINDS = new Set([REQUEST_KIND.CANCEL, "flight_cancel", "activity_cancel"]);

const rawKindOf = (x) => (typeof x === "string" ? x : x?.kind || x?.root?.kind || x?.latest?.kind);

/** The kind of a request, a chain ({ root }) or a kind string; "modify" by default. */
export const kindOf = (x) => (CANCEL_KINDS.has(rawKindOf(x)) ? REQUEST_KIND.CANCEL : REQUEST_KIND.MODIFY);

/** Is this an activity request (or chain)? */
export const isActivityRequest = (x) => String(rawKindOf(x) || "").startsWith("activity_");

export const kindMeta = (x) => PRODUCT_META[rawKindOf(x)] || KIND_META[kindOf(x)];
